import { useState } from 'react';
import { Building2 } from 'lucide-react';

/**
 * CompanyLogo — tries stored logo, then Clearbit guess, then building icon fallback.
 * All state-managed in React so onError works properly.
 */
const guessLogoUrl = (company) => {
  if (!company) return null;
  let domain = company.trim().toLowerCase();
  for (const s of [' inc', ' inc.', ' llc', ' ltd', ' corp', ' corporation', ' co', ' company', ' group', ' technologies', ' tech']) {
    if (domain.endsWith(s)) domain = domain.slice(0, -s.length).trim();
  }
  domain = domain.replace(/[\s,]+/g, '').replace(/&/g, 'and');
  // Google's favicon service — free, reliable, returns 128px icons
  return `https://www.google.com/s2/favicons?domain=${domain}.com&sz=128`;
};

export default function CompanyLogo({ company, logo, size = 'md', className = '' }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [guessFailed, setGuessFailed] = useState(false);

  const sizes = {
    sm: { box: 'w-8 h-8', icon: 'w-4 h-4', radius: 'rounded-lg' },
    md: { box: 'w-10 h-10', icon: 'w-5 h-5', radius: 'rounded-lg' },
    lg: { box: 'w-12 h-12', icon: 'w-6 h-6', radius: 'rounded-xl' },
  };
  const s = sizes[size] || sizes.md;

  // 1. Try stored logo
  if (logo && !imgFailed) {
    return (
      <img
        src={logo}
        alt={company}
        className={`${s.box} ${s.radius} object-contain bg-slate-50 p-1 flex-shrink-0 ${className}`}
        onError={() => setImgFailed(true)}
      />
    );
  }

  // 2. Try Clearbit guess
  const guessUrl = guessLogoUrl(company);
  if (guessUrl && !guessFailed) {
    return (
      <img
        src={guessUrl}
        alt={company}
        className={`${s.box} ${s.radius} object-contain bg-slate-50 p-1 flex-shrink-0 ${className}`}
        onError={() => setGuessFailed(true)}
      />
    );
  }

  // 3. Fallback icon
  return (
    <div className={`${s.box} ${s.radius} bg-slate-100 flex items-center justify-center flex-shrink-0 ${className}`}>
      <Building2 className={`${s.icon} text-slate-400`} />
    </div>
  );
}
